import { authorizedFetch } from "~/auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";

const RUNTIME_API_BASE = import.meta.env.VITE_FGA_API_URL || "/fga-api/api";
const MANAGEMENT_API_BASE =
  import.meta.env.VITE_MANAGEMENT_API_URL || "/management-api";

export interface ActivityTuple {
  user: string;
  relation: string;
  object: string;
  action: string;
}

export interface ActorInfo {
  username: string | null;
  displayName: string | null;
  email: string | null;
}

export interface ActivityEvent {
  id: string;
  operation: string;
  outcome: string;
  actor: string;
  actorClient: string | null;
  traceId: string | null;
  errorCode: string | null;
  occurredAt: string;
  tuples: ActivityTuple[];
  actorInfo: ActorInfo | null;
  source: "access" | "management";
}

export interface ActivityPage {
  events: ActivityEvent[];
  nextCursor: string | null;
}

export interface ActivityFilters {
  cursor?: string;
  limit?: number;
  from?: string;
  to?: string;
  actor?: string;
  operation?: string;
  outcome?: string;
  object?: string;
  objectPrefix?: string;
  root?: string;
}

function normalizeEvent(raw: Record<string, unknown>): ActivityEvent {
  const tuples = (raw.Tuples ?? raw.tuples) as
    | Record<string, unknown>[]
    | undefined;
  const actorInfo = (raw.ActorInfo ?? raw.actorInfo) as
    | Record<string, unknown>
    | null
    | undefined;
  return {
    id: (raw.Id ?? raw.id) as string,
    operation: (raw.Operation ?? raw.operation) as string,
    outcome: (raw.Outcome ?? raw.outcome) as string,
    actor: (raw.Actor ?? raw.actor) as string,
    actorClient: (raw.ActorClient ?? raw.actorClient) as string | null,
    traceId: (raw.TraceId ?? raw.traceId) as string | null,
    errorCode: (raw.ErrorCode ?? raw.errorCode) as string | null,
    occurredAt: (raw.OccurredAt ?? raw.occurredAt) as string,
    tuples: normalizeTuples(tuples),
    actorInfo: normalizeActorInfo(actorInfo),
    source: (raw.source ?? "access") as "access" | "management",
  };
}

function normalizeTuples(
  raw: Record<string, unknown>[] | undefined,
): ActivityTuple[] {
  if (!raw) return [];
  return raw.map((t) => ({
    user: (t.User ?? t.user) as string,
    relation: (t.Relation ?? t.relation) as string,
    object: (t.Object ?? t.object) as string,
    action: (t.Action ?? t.action) as string,
  }));
}

function normalizeActorInfo(
  raw: Record<string, unknown> | null | undefined,
): ActorInfo | null {
  if (!raw) return null;
  return {
    username: (raw.Username ?? raw.username) as string | null,
    displayName: (raw.DisplayName ?? raw.displayName) as string | null,
    email: (raw.Email ?? raw.email) as string | null,
  };
}

async function fetchActivity(
  apiBase: string,
  source: "access" | "management",
  filters: ActivityFilters = {},
): Promise<ActivityPage> {
  const params = new URLSearchParams();

  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.actor) params.set("actor", filters.actor);
  if (filters.operation) params.set("operation", filters.operation);
  if (filters.outcome) params.set("outcome", filters.outcome);
  if (filters.object) params.set("object", filters.object);
  if (filters.objectPrefix) params.set("objectPrefix", filters.objectPrefix);
  if (filters.root) params.set("root", filters.root);

  const qs = params.toString();
  const url = `${apiBase}/activity${qs ? `?${qs}` : ""}`;

  const res = await authorizedFetch(url);
  await throwIfNotOk(res);
  const raw = (await res.json()) as {
    Events?: unknown[];
    events?: unknown[];
    NextCursor?: string | null;
    nextCursor?: string | null;
  };
  const rawEvents = raw.Events ?? raw.events ?? [];
  return {
    events: rawEvents.map((e) => ({
      ...normalizeEvent(e as Record<string, unknown>),
      source,
    })),
    nextCursor: raw.NextCursor ?? raw.nextCursor ?? null,
  };
}

export async function getRuntimeActivity(
  filters: ActivityFilters = {},
): Promise<ActivityPage> {
  return fetchActivity(RUNTIME_API_BASE, "access", filters);
}

export async function getManagementActivity(
  filters: ActivityFilters = {},
): Promise<ActivityPage> {
  return fetchActivity(MANAGEMENT_API_BASE, "management", filters);
}
